import { siteConfig } from "@/components/homepage/_config"
import { SectionHeader } from "@/components/homepage/ui/SectionHeader"
import { Icons } from "@/components/homepage/ui/Icons"
import { HeaderBadge } from "@/components/homepage/ui/HeaderBadge"
import { OrbitingIconsBurst } from "@/components/homepage/animations/orbiting-icons-burst"
import { DottedMap } from "@/components/ui/dotted-map"
import { DitherDivider } from "@/components/homepage/animations/lazy-dither"
import { CornerPlus } from "@/components/ui/corner-plus"
import { Video, Sparkles, Film, Clapperboard, Camera, Wand2 } from "lucide-react"

const featureConfig = siteConfig.featureSection

export function Feature() {
    return (
        <section id="features" className="w-full relative">
            <DitherDivider />
            <SectionHeader>
                <div className="flex flex-col items-center justify-center">
                    <HeaderBadge icon={featureConfig.badge.icon} text={featureConfig.badge.text} />
                    <div className="flex flex-col items-center justify-center gap-4 mt-4">
                        <h2 className="text-3xl md:text-4xl lg:text-6xl font-medium tracking-tighter text-center text-balance">
                            {featureConfig.title}
                        </h2>
                        <p className="text-muted-foreground md:text-lg text-center text-balance mx-auto">
                            {featureConfig.description}
                        </p>
                    </div>
                </div>
            </SectionHeader>
            <DitherDivider />
            <div className="mx-auto">
                {/* Centered header */}
                <div className="text-center px-6 md:px-10 lg:px-14 pt-14 md:pt-20 pb-10 border-t border-border">
                    <h3 className="text-3xl font-medium tracking-tighter md:text-4xl">
                        {featureConfig.sections.title}
                    </h3>
                    <p className="mt-4 text-muted-foreground text-balance max-w-md mx-auto">
                        {featureConfig.sections.description}
                    </p>
                </div>

                {/* Two-column grid with divider */}
                <div className="grid grid-cols-1 md:grid-cols-2 md:divide-x divide-border border-t border-border">
                    <div className="relative border-b border-border flex flex-col">
                        <CornerPlus position="top-right" className="text-muted-foreground/50 hidden md:block" />
                        <OrbitingIconsBurst
                            items={[
                                { id: 1, delay: 0, rayIndex: 10, distance: 152, className: "", icon: <Video className="size-full" /> },
                                { id: 2, delay: 0.08, rayIndex: 14, distance: 138, className: "", icon: <Sparkles className="size-full" /> },
                                { id: 3, delay: 0.16, rayIndex: 19, distance: 178, className: "", icon: <Film className="size-full" /> },
                                { id: 4, delay: 0.24, rayIndex: 22, distance: 144, className: "", icon: <Clapperboard className="size-full" /> },
                                { id: 5, delay: 0.48, rayIndex: 4, distance: 156, className: "", icon: <Camera className="size-full" /> },
                                { id: 6, delay: 0.56, rayIndex: 7, distance: 170, className: "", icon: <Wand2 className="size-full" /> },
                            ]}
                            lineCount={22}
                            centerIcon={<Icons.video className="size-full" />}
                            className="relative min-h-[300px] md:min-h-[400px] mask-[radial-gradient(ellipse_at_center,black_40%,black_60%,transparent_85%)]"
                        />
                        <div className="max-w-xl text-left items-start p-6 pb-14 mt-auto">
                            <p className="text-sm text-muted-foreground flex items-center gap-3 justify-start">
                                {featureConfig.sections.blocks[0].icon}
                                {featureConfig.sections.blocks[0].title}
                            </p>
                            <p className="text-base text-foreground leading-relaxed mt-2">
                                {featureConfig.sections.blocks[0].description}
                            </p>
                        </div>
                    </div>

                    <div className="relative border-b border-border flex flex-col">
                        <CornerPlus position="top-left" className="text-muted-foreground/50 hidden md:block" />
                        <div className="relative min-h-[300px] md:min-h-[400px] overflow-hidden mask-[radial-gradient(ellipse_at_center,black_40%,black_60%,transparent_85%)]">
                            <div className="absolute inset-0 bg-radial from-transparent to-background to-70%" />
                            <DottedMap
                                dotRadius={0.35}
                                className="text-black"
                                markers={[
                                    { lat: 40.7128, lng: -74.006, size: 0.3 },
                                    { lat: 34.0522, lng: -118.2437, size: 0.3 },
                                    { lat: 51.5074, lng: -0.1278, size: 0.3 },
                                    { lat: -33.8688, lng: 151.2093, size: 0.3 },
                                    { lat: 48.8566, lng: 2.3522, size: 0.3 },
                                    { lat: 35.6762, lng: 139.6503, size: 0.3 },
                                    { lat: 55.7558, lng: 37.6176, size: 0.3 },
                                    { lat: 39.9042, lng: 116.4074, size: 0.3 },
                                    { lat: 28.6139, lng: 77.209, size: 0.3 },
                                    { lat: -23.5505, lng: -46.6333, size: 0.3 },
                                    { lat: 1.3521, lng: 103.8198, size: 0.3 },
                                    { lat: 25.2048, lng: 55.2708, size: 0.3 },
                                    { lat: 52.52, lng: 13.405, size: 0.3 },
                                    { lat: 19.4326, lng: -99.1332, size: 0.3 },
                                    { lat: -26.2041, lng: 28.0473, size: 0.3 },
                                ]}
                            />
                        </div>
                        <div className="max-w-xl text-left items-start p-6 pb-14 mt-auto">
                            <p className="text-sm text-muted-foreground flex items-center gap-3 justify-start">
                                {featureConfig.sections.blocks[1].icon}
                                {featureConfig.sections.blocks[1].title}
                            </p>
                            <p className="text-base text-foreground leading-relaxed mt-2">
                                {featureConfig.sections.blocks[1].description}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <DitherDivider />
        </section>
    )
}
